import { globalClasses } from "helper/globalClasses";
import { globalVariables } from "helper/globalVariables";
import { useEffect, useRef, useState, useContext } from "react";
import { useMediaQuery } from "react-responsive";
import Dropdown from "shared/Components/dropdown";
import SearchBar from "shared/Components/search";
import ThreeDotMenu from "shared/Components/threeDotMenu";
import { TableWithPagination } from "shared/Components/tableWithPagination/index.js";
import { Button } from "shared/Components/button";
import { ModalCard } from "shared/Components/modalCard";
import { useNavigate } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import {
  deleteDogProfileMutation,
  getAllRegisteredDogsQuery,
  updateRegisteredDogStatusMutation,
} from "redux/features/superAdminSlice/manageDogsSlice/manageDogsGql";
import { client } from "helper/apolloConfig";
import { PopUpMessages } from "shared/Components/popUpMessages";
import { useDispatch } from "react-redux";
import { setDogProfile } from "redux/features/superAdminSlice/manageDogsSlice/manageDogsSlice";
import Loader from "shared/Components/loader";
import _debounce from "lodash/debounce";
import { RoleContext } from "context/RoleContext";
import PageInfo from "shared/Components/pageInfo";

const statusData = [
  globalVariables.active,
  globalVariables.retired,
  globalVariables.deceased,
];

const threeDotData = [
  globalVariables.editProfile,
  // globalVariables.addDogHistory,
  // globalVariables.editDogHistory,
  globalVariables.viewDogHistory,
  globalVariables.deleteProfile,
];

const headData = [
  globalVariables.dogId,
  globalVariables.dogName,
  globalVariables.breed,
  globalVariables.ownerName,
  // globalVariables.titleDate,
  // globalVariables.title,
  // globalVariables.qs,
  globalVariables.dogStatus,

  // globalVariables.status,
];

export const DetailsOfDog = ({ editable = true }) => {

  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [apiData, setApiData] = useState({
    title: "",
    desc: "",
    type: "pending",
  });
  const { tableDataCls, paginatedDataCls } = globalClasses;
  const mobileRef = useRef();
  const modalRef = useRef();
  const navigate = useNavigate();
  const [getData, { loading, error, data }] = useLazyQuery(
    getAllRegisteredDogsQuery,
    { fetchPolicy: "network-only" }
  );
  const resultData = data?.getAllRegisteredDogs;
  const dogs = resultData?.data || [];

  const dispatch = useDispatch();
  const [statusLoading, setStatusLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const role = useContext(RoleContext);

  useEffect(() => {
    handlePaginationData({});
  }, []);

  const handlePaginationData = ({ page = 1, size = 10, search }) => {
    getData({
      variables: {
        pageNumber: page,
        pageSize: size,
        search,
      },
    });
  };

  const debouncedHandleSearch = _debounce((value) => {
    handlePaginationData({ search: value });
  }, 1000); // Adjust the debounce delay as needed

  const onSelectOption = async (option, data) => {
    switch (option) {
      case globalVariables.addDogHistory:
        navigate("/admin/manage/dogs/addHistory");
        break;
      case globalVariables.editProfile:
        dispatch(setDogProfile(data));
        navigate("/admin/manage/dogs/editProfile");
        break;
      case globalVariables.viewDogHistory: {
        navigate("/admin/manage/dogs/dogHistory?option=switchRole", {
          state: {
            dogId: data?.id,
            newDogId: data?.dog_id,
            ownerName: data?.owner_name,
          },
        });
        break;
      }

      case globalVariables.deleteProfile: {
        setDeleteLoading(true);
        try {
          const result = await client.mutate({
            mutation: deleteDogProfileMutation,
            variables: { id: data?.id },
          });

          setDeleteLoading(false);
          if (result?.data?.deleteRegisteredDogs) {
            modalRef.current.openModal();
            setApiData({
              ...apiData,
              title: globalVariables.success,
              desc: result?.data?.deleteRegisteredDogs?.message,
              type: "success",
            });
          }
        } catch (error) {
          setDeleteLoading(false);
          modalRef.current.openModal();
          setApiData({
            ...apiData,
            title: globalVariables.error,
            desc: error?.message,
            type: "pending",
          });
        }
        break;
      }
      default:
        break;
    }
  };

  const onSelectStatus = (option, ind) => {
    const data = [...dogs];
    const obj = { ...data[ind] };
    obj.status = option;
    data[ind] = obj;

    onChangeStatus(obj.id, option);
  };

  const onCloseModal = () => {
    if (apiData.type === "success") {
      handlePaginationData({});
    }
  };

  const onChangeSearchName = (event) => {
    const value = event?.target?.value;
    setSearch(value);
    if (value) {
      debouncedHandleSearch(value);
    } else {
      handlePaginationData({ page });
    }
  };

  const debouncedSearch = _debounce(onChangeSearchName, 1000);

  const onChangeStatus = async (id, status) => {
    setStatusLoading(true);
    try {
      const result = await client.mutate({
        mutation: updateRegisteredDogStatusMutation,
        variables: { id: id, status: status },
      });

      setStatusLoading(false);
      if (result?.data?.updateRegisteredDogStatus) {
        modalRef.current.openModal();
        setApiData({
          ...apiData,
          title: globalVariables.success,
          desc: result?.data?.updateRegisteredDogStatus?.message,
          type: "success",
        });
      }
    } catch (error) {
      setStatusLoading(false);
      modalRef.current.openModal();
      setApiData({
        ...apiData,
        title: globalVariables.error,
        desc: error?.message,
        type: "pending",
      });
    }
  };

  const renderTableBody = () => {
    return dogs.map((data, ind) => (
      <tr
        key={data?.call_name}
        className={role === 'Super Admin' ? "cursor-pointer" : "cursor-default"}
        onClick={() => {
          if (editable) {
            navigate("/admin/manage/dogs/dogHistory", {
              state: {
                dogId: data?.id,
                newDogId: data?.dog_id,
                ownerName: data?.owner_name,
              },
            });
          }
        }}
      >
        <td className={paginatedDataCls(ind)}>{data?.dog_id ?? "-"}</td>
        <td className={paginatedDataCls(ind)}>{data?.call_name ?? "-"}</td>
        <td className={paginatedDataCls(ind)}>{data?.breed_mix || data?.breed}</td>
        <td className={paginatedDataCls(ind)}>{data?.owner_name ?? "-"}</td>
        {/* <td className={paginatedDataCls(ind)}>
          {data?.titleDate ? moment(data?.titleDate).format("DD/MM/YYYY") : "-"}
        </td>
        <td className={paginatedDataCls(ind)}>{data?.title ?? "-"}</td>
        <td className={paginatedDataCls(ind)}>
          {data?.qs === null
            ? "-"
            : data?.qs
            ? globalVariables.yes
            : globalVariables.no}
        </td> */}
        <td className={paginatedDataCls(ind) + " first-letter:capitalize"}>{data?.status ?? "-"}</td>
        {editable && (
          <td className={paginatedDataCls(ind)}>
            <div
              className="flex  items-center justify-center"
              onClick={() => setSelectedIndex(ind)}
            >
              {/* {statusLoading && ind === selectedIndex ? (
              <Loader />
            ) : (
              <Dropdown
                dropdownOptions={statusData}
                placeholder={globalVariables.status}
                width={"w-[120px]"}
                defaultOption={data?.status ?? statusData[0]}
                onChange={(option) => onChangeStatus(data?.id, option)}
              />
            )}
            <div className="mr-6" /> */}
              {deleteLoading && ind === selectedIndex ? (
                <Loader />
              ) : (
                <ThreeDotMenu
                  onClickDot={() => setSelectedIndex(ind)}
                  options={threeDotData}
                  dynamicHeight
                  onSelectOption={(option) => {
                    onSelectOption(option, data);
                  }}
                  threeDotWidth="w-[20px]"
                />
              )}
            </div>
          </td>
        )}
      </tr>
    ));
  };

  const renderMobileTableBody = () => {
    return dogs.map((data, ind) => (
      <div key={data?.call_name} className="mb-5">
        <div className="flex flex-col p-4 border-b border-gray-200">
          <div className="flex flex-row justify-between">
            <span className="text-base font-semibold text-gray-900">
              {`${globalVariables.dog}: ${data?.call_name}`}
            </span>
            {/* <ThreeDotMenu
              options={statusData}
              onSelectOption={(option) => onSelectStatus(option, ind)}
              toggleComponent={
                <div className="py-0.5 px-2 rounded-2xl bg-gray-50 border border-gray-200 flex flex-row">
                  {data?.status ?? statusData[0]}
                  <div className="mr-1" />
                  <img
                    src={arrowDown}
                    height={16}
                    width={16}
                    alt=""
                    className="transform rotate-180"
                  />
                </div>
              }
              dynamicHeight="h-[155px]"
            /> */}
          </div>
          <div className="mt-1" />
          <span className="text-sm font-normal text-gray-600">
            {`${globalVariables.breed}: ${data?.breed}`}
          </span>
          <div className="flex flex-row justify-between items-center mt-3">
            <span className="text-sm font-normal text-gray-600">
              {`${globalVariables.ownerName}: ${data?.owner_name}`}
            </span>
            {editable ? (
              <Button
                onClick={() => {
                  mobileRef.current.openModal();
                  setSelectedIndex(ind);
                }}
                label={globalVariables.viewMore}
                width="w-[104px]"
              />
            ) : (
              <span className="text-sm font-normal text-gray-600">{`${
                globalVariables.status
              }: ${data?.status ?? "-"}`}</span>
            )}
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className="App rounded-md min-h-screen lg:ml-[17rem] sm:pt-8 bg-white lg:p-10">
      <PageInfo title={globalVariables.dogDetails} />
        <div className="flex flex-col gap-4 visible min-[480px]:hidden mb-6">
          <span className="text-xl font-semibold text-gray-900 ">
            {globalVariables.detailsDogs}
          </span>
          <SearchBar
            width="w-full"
            onChange={debouncedSearch}
            placeholder={globalVariables.search}
          />
        </div>
        {isMobile ? (
          <TableWithPagination
            tableBody={renderMobileTableBody()}
            loading={loading}
            error={error}
            itemsPerPage={10}
            totalPage={resultData?.totalPages}
            handlePageChange={(page) => {
              setPage(page);
              handlePaginationData({ page, search });
            }}
          />
        ) : (
          <TableWithPagination
            tableHeight={"md:h-[calc(100vh-10rem)] lg:h-[100vh]"}
            headerComponent={
              <SearchBar
                onChange={debouncedSearch}
                placeholder={globalVariables.search}
              />
            }
            headData={editable ? [...headData, ""] : headData}
            tableBody={renderTableBody()}
            loading={loading}
            error={error}
            itemsPerPage={10}
            totalPage={resultData?.totalPages}
            handlePageChange={(page) => {
              setPage(page);
              handlePaginationData({ page, search });
            }}
            overflow="overflow-visible"
          />
        )}
      <ModalCard
        ref={mobileRef}
        maxHeight="max-h-[800px]"
        title={globalVariables.manageDogs}
        children={
          <div className="flex flex-row justify-between mt-4 items-center max-[380px]:mr-8 mr-0">
            {/* {statusLoading ? (
              <Loader />
            ) : (
              <Dropdown
                dropdownOptions={statusData}
                placeholder={globalVariables.status}
                width={"w-[120px]"}
                defaultOption={dogs?.[selectedIndex]?.status ?? statusData[0]}
                onChange={(option) =>
                  onChangeStatus(dogs?.[selectedIndex]?.id, option)
                }
              />
            )} */}
            {deleteLoading ? (
              <Loader />
            ) : (
              <Dropdown
                dropdownOptions={threeDotData}
                placeholder={globalVariables.action}
                width={"w-[120px]"}
                onChange={(option) =>
                  onSelectOption(option, dogs[selectedIndex])
                }
              />
            )}
          </div>
        }
        data={[
          {
            key: globalVariables.dog,
            value: dogs[selectedIndex]?.call_name,
          },
          {
            key: globalVariables.breed,
            value: dogs[selectedIndex]?.breed,
          },
          {
            key: globalVariables.owner,
            value: dogs[selectedIndex]?.owner_name,
          },
          // {
          //   key: globalVariables.titleDate,
          //   value: dogs[selectedIndex]?.titleDate,
          // },
          // {
          //   key: globalVariables.title,
          //   value: dogs[selectedIndex]?.title,
          // },
          // {
          //   key: globalVariables.qs,
          //   value:
          //     dogs[selectedIndex]?.qs === null
          //       ? "-"
          //       : dogs[selectedIndex]?.qs
          //       ? globalVariables.yes
          //       : globalVariables.no,
          // },
          {
            key: globalVariables.dogLegs,
            value: dogs[selectedIndex]?.dogLegs ?? "-",
          },
        ]}
      />
      <PopUpMessages
        title={apiData.title}
        desc={apiData.desc}
        type={apiData.type}
        ref={modalRef}
        onCloseModal={onCloseModal}
      />
    </div>
  );
};
